import * as React from 'react';
import { useIntl } from 'react-intl';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';

import { CellDatetime, DataTable, DataTableColumnHeader } from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { subItemUsageRecordMessages } from 'src/features/subscriptionItemUsageRecords/utils/messages';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { SimulationUsageRecord } from 'src/features/billingSimulation/utils/types';

type BillingSimulationUsageRecordsTableProps = {
    records: SimulationUsageRecord[];
    isLoading?: boolean;
};

const BillingSimulationUsageRecordsTable = ({ records, isLoading }: BillingSimulationUsageRecordsTableProps) => {
    const { formatMessage } = useIntl();

    const columnHelper = React.useMemo(() => createColumnHelper<SimulationUsageRecord>(), []);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('recorded_at', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDatetime,
                meta: { title: formatMessage(subItemQuantityEventMessages.recordedAt) },
            }),
            columnHelper.accessor('service_plan_item_name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.service_plan_item_name || '-',
                meta: { title: formatMessage(billingSimulationMessages.item) },
            }),
            columnHelper.accessor('billing_period_start', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDate,
                meta: { title: formatMessage(subItemUsageRecordMessages.billingPeriodStart) },
            }),
            columnHelper.accessor('billing_period_end', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDate,
                meta: { title: formatMessage(subItemUsageRecordMessages.billingPeriodEnd) },
            }),
            columnHelper.accessor('quantity', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.quantity,
                meta: { title: formatMessage(subItemQuantityEventMessages.quantity) },
            }),
        ],
        [columnHelper, formatMessage],
    );

    const data = React.useMemo(
        () => ({ count: records.length, next: null, previous: null, results: records }),
        [records],
    );

    return (
        <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            enableSearchParams={false}
            getSortedRowModel={getSortedRowModel()}
            getRowId={(row) => row.id}
            semantics={{
                title: formatMessage(subItemUsageRecordMessages.usageRecords),
                entryName: formatMessage(subItemUsageRecordMessages.record).toLowerCase(),
                entryNames: formatMessage(subItemUsageRecordMessages.records).toLowerCase(),
            }}
        />
    );
};

export default BillingSimulationUsageRecordsTable;
